import {
  type AuditExportQuery,
  type ExportKind,
  type LeadsExportQuery,
  type MerchantsExportQuery,
  type SaveReportSettings,
  overviewCompareSchema,
  reportSettingsSchema,
  weeklyPreviewSchema,
  weeklyReportSchema,
  weeklySendResultSchema,
} from '@arabat/contracts';
import type { z } from 'zod';
import { NetworkError, toApiError } from './errors.js';

type Request = <T extends z.ZodType | undefined = undefined>(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  opts?: { body?: unknown; schema?: T },
) => Promise<T extends z.ZodType ? z.output<T> : unknown>;

/** A downloaded export: the file, its name, and how many rows it holds of how many matched. */
export interface ExportDownload {
  blob: Blob;
  filename: string;
  rows: number;
  total: number;
}

export type ExportQuery = {
  merchants: MerchantsExportQuery;
  leads: LeadsExportQuery;
  audit: AuditExportQuery;
};

function params(query: object): string {
  const p = new URLSearchParams();
  for (const [key, value] of Object.entries(query))
    if (value !== undefined && value !== null && value !== '') p.set(key, String(value));
  return p.toString();
}

/** Platform reports and Excel exports (ADR-0158), `api.reportsOps`. */
export function reportsOpsClient(deps: {
  request: Request;
  doFetch: typeof fetch;
  baseUrl: string;
}) {
  const { request, doFetch, baseUrl } = deps;
  return {
    weekly: () => request('GET', '/platform/reports/weekly', { schema: weeklyReportSchema }),
    preview: () =>
      request('GET', '/platform/reports/weekly/preview', { schema: weeklyPreviewSchema }),
    sendNow: () =>
      request('POST', '/platform/reports/weekly/send', {
        body: {},
        schema: weeklySendResultSchema,
      }),
    saveSettings: (body: SaveReportSettings) =>
      request('PUT', '/platform/reports/settings', { body, schema: reportSettingsSchema }),
    overviewCompare: () =>
      request('GET', '/platform/reports/overview-compare', { schema: overviewCompareSchema }),
    /** Fetches an .xlsx export; the page saves the blob and shows the row counts. */
    async exportFile<K extends ExportKind>(
      kind: K,
      query: ExportQuery[K],
    ): Promise<ExportDownload> {
      let response: Response;
      try {
        response = await doFetch(`${baseUrl}/platform/reports/export/${kind}?${params(query)}`, {
          method: 'GET',
          credentials: 'same-origin',
        });
      } catch (error) {
        throw new NetworkError(error);
      }
      if (!response.ok) throw await toApiError(response);
      const disposition = response.headers.get('content-disposition') ?? '';
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `arabat-${kind}.xlsx`;
      return {
        blob: await response.blob(),
        filename,
        rows: Number(response.headers.get('x-export-rows') ?? 0),
        total: Number(response.headers.get('x-export-total') ?? 0),
      };
    },
  };
}
